import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    ArrowLeft,
    ArrowRight,
    ShieldCheck,
} from "lucide-react";
import { Navbar } from "../components/sections/Navbar";
import { Footer } from "../components/sections/Footer";
import { FloatingContact } from "../components/visuals/FloatingContact";
import { ScrollProgress } from "../components/visuals/ScrollProgress";
import useReveal from "../lib/useReveal";

// Product catalog — expand this list as devices launch.
const PRODUCTS = [
    {
        id: "equipment-alerts",
        category: "alert-automation",
        name: "Equipment Alerts",
        tag: "Alert & Automation",
        summary:
            "Turn a single press into a work order. Ideal for shop floors, warehouses, and manufacturing lines that need a fast, auditable escalation path.",
        interest: "alert-automation",
        detailsHref: "/alert-automation#equipment-alerts",
        icon: null,
        media: {
            type: "image",
            src: "https://customer-assets-m6fa6gv7.emergentagent.net/job_narcan-dispenser-pro/artifacts/xtr57xxf_03e71a79-6d3a-463e-8591-b443fe462013.png",
            alt: "Kairos Equipment Alert device",
        },
    },
    {
        id: "automated-weight",
        category: "alert-automation",
        name: "Automated Weight Alerts",
        tag: "Alert & Automation",
        summary:
            "Industrial-grade scale + Kairos platform. Continuously tracks tank, cylinder, chemical, or consumable weight and alerts before you run out.",
        interest: "alert-automation",
        detailsHref: "/alert-automation#automated-weight-alerts",
        icon: null,
        media: {
            type: "video",
            src: "https://customer-assets-m6fa6gv7.emergentagent.net/job_narcan-dispenser-pro/artifacts/9167zdcx_Industrial_scale_transforming_co%E2%80%A6_202608241818.mp4",
        },
    },
    {
        id: "overdose-response",
        category: "public-safety",
        name: "Overdose Response Device",
        tag: "Public Safety",
        summary:
            "Rugged connected dispenser that provides rapid access to over-the-counter Naloxone in the moments a community wants it available.",
        interest: "overdose-response",
        detailsHref: "/public-safety/overdose-response",
        icon: null,
        media: {
            type: "image",
            src: "https://customer-assets-m6fa6gv7.emergentagent.net/job_narcan-dispenser-pro/artifacts/oc171or9_image.png",
            alt: "Kairos Overdose Response Device — Naloxone-inside dispenser with emergency press button",
            fit: "contain",
            bg: "bg-white",
        },
    },
];

const FILTERS = [
    { id: "all", label: "All Products" },
    { id: "public-safety", label: "Public Safety" },
    { id: "alert-automation", label: "Alert & Automation" },
];

export default function ProductsPage() {
    useReveal();
    const [filter, setFilter] = useState("all");
    const visible = useMemo(
        () =>
            filter === "all"
                ? PRODUCTS
                : PRODUCTS.filter((p) => p.category === filter),
        [filter]
    );

    return (
        <main data-testid="products-page" className="min-h-screen bg-white">
            <ScrollProgress />
            <Navbar />

            {/* Hero */}
            <section className="relative bg-[#0A1428] text-white pt-32 md:pt-40 pb-20 md:pb-24 overflow-hidden">
                <div className="absolute inset-0 dot-grid-light opacity-25 pointer-events-none" />
                <div className="absolute -top-32 -right-24 w-[520px] h-[520px] opacity-[0.08] pointer-events-none float-slow">
                    <svg viewBox="0 0 200 200">
                        <polygon points="20,160 130,60 180,160" fill="#F0493E" />
                    </svg>
                </div>

                <div className="relative max-w-7xl mx-auto px-6 md:px-12">
                    <Link
                        to="/"
                        data-testid="products-back"
                        className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.2em] text-slate-300 hover:text-white transition-colors mb-10"
                    >
                        <ArrowLeft size={16} strokeWidth={2} />
                        Back to Kairos
                    </Link>

                    <div className="text-xs md:text-sm font-bold uppercase tracking-[0.28em] text-[#F0493E]">
                        Kairos Products
                    </div>
                    <h1 className="mt-6 font-display font-extrabold text-4xl md:text-6xl lg:text-7xl leading-[1] tracking-tighter text-balance max-w-4xl">
                        Every button
                        <br />
                        Kairos builds.
                    </h1>
                    <p className="mt-8 max-w-2xl text-lg md:text-xl text-slate-300 leading-relaxed font-medium">
                        Browse the Kairos device catalog. Every product is
                        built around a specific problem — inquire about any
                        device and we&apos;ll show you exactly how it fits
                        your operation.
                    </p>
                </div>
            </section>

            {/* Filter tabs */}
            <section className="relative bg-white border-b border-slate-200 sticky top-20 z-30 backdrop-blur-xl bg-white/85">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div
                        data-testid="products-filters"
                        className="flex flex-wrap gap-2 py-4"
                    >
                        {FILTERS.map((f) => {
                            const active = f.id === filter;
                            return (
                                <button
                                    key={f.id}
                                    data-testid={`products-filter-${f.id}`}
                                    onClick={() => setFilter(f.id)}
                                    className={`px-4 md:px-5 py-2.5 text-[11px] md:text-xs font-bold uppercase tracking-[0.22em] transition-colors ${
                                        active
                                            ? "bg-[#0A1428] text-white"
                                            : "bg-white text-slate-600 hover:text-[#0A1428] border border-slate-200"
                                    }`}
                                >
                                    {f.label}
                                </button>
                            );
                        })}
                        <div className="ml-auto hidden md:flex items-center text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500">
                            {visible.length} product{visible.length !== 1 && "s"}
                        </div>
                    </div>
                </div>
            </section>

            {/* Grid */}
            <section className="relative bg-white py-16 md:py-24">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div
                        data-testid="products-grid"
                        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
                    >
                        {visible.map((p) => (
                            <ProductCard key={p.id} product={p} />
                        ))}
                    </div>
                </div>
            </section>

            {/* Talk to us strip */}
            <section className="relative bg-slate-50 py-20 md:py-24 border-y border-slate-200">
                <div className="max-w-6xl mx-auto px-6 md:px-12 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
                    <div>
                        <div className="text-xs md:text-sm font-bold uppercase tracking-[0.28em] text-[#F0493E]">
                            Don&apos;t see it?
                        </div>
                        <h2 className="mt-6 font-display font-extrabold text-3xl md:text-5xl leading-[1] tracking-tighter text-[#0A1428] text-balance max-w-2xl">
                            Tell us the problem.
                            <br />
                            We&apos;ll build around it.
                        </h2>
                    </div>
                    <Link
                        to="/contact?interest=custom-system"
                        data-testid="products-custom-cta"
                        className="btn-primary inline-flex items-center justify-center gap-3 px-8 py-4 text-sm font-bold tracking-wide uppercase rounded-sm self-start md:self-end"
                    >
                        Request a custom build
                        <ArrowRight size={18} strokeWidth={2} />
                    </Link>
                </div>
            </section>

            <Footer />
            <FloatingContact />
        </main>
    );
}

const ProductCard = ({ product }) => {
    const inquireHref = `/contact?interest=${product.interest}&device=${encodeURIComponent(
        product.name
    )}`;

    return (
        <div
            data-testid={`product-card-${product.id}`}
            className="group flex flex-col bg-white border border-slate-200 hover:border-[#0A1428] transition-colors overflow-hidden"
        >
            {/* Media */}
            <div
                className={`relative aspect-[4/3] overflow-hidden ${
                    product.media.bg || "bg-[#0A1428]"
                }`}
            >
                {product.media.type === "image" && (
                    <img
                        src={product.media.src}
                        alt={product.media.alt}
                        className={`w-full h-full ${
                            product.media.fit === "contain"
                                ? "object-contain p-6"
                                : "object-cover"
                        }`}
                        loading="lazy"
                    />
                )}
                {product.media.type === "video" && (
                    <video
                        src={product.media.src}
                        className="w-full h-full object-cover"
                        autoPlay
                        loop
                        muted
                        playsInline
                    />
                )}
                {product.media.type === "icon" && product.icon && (
                    <div className="w-full h-full flex items-center justify-center relative">
                        <div className="absolute inset-0 dot-grid-light opacity-25" />
                        <div className="relative h-24 w-24 grid place-items-center border border-white/25 bg-white/5 backdrop-blur-sm">
                            <product.icon
                                size={38}
                                strokeWidth={1.25}
                                className="text-[#F0493E]"
                            />
                        </div>
                    </div>
                )}
                <div className="absolute top-3 left-3 bg-white/95 text-[#0A1428] text-[10px] font-bold uppercase tracking-[0.22em] px-2.5 py-1">
                    {product.tag}
                </div>
            </div>

            {/* Body */}
            <div className="flex flex-col flex-1 p-6 md:p-7">
                <h3 className="font-display font-extrabold text-xl md:text-2xl text-[#0A1428] leading-tight tracking-tight">
                    {product.name}
                </h3>
                <p className="mt-3 text-sm md:text-[15px] text-slate-600 leading-relaxed font-medium flex-1">
                    {product.summary}
                </p>

                {/* Actions — inquire + details */}
                <div className="mt-6 flex items-center gap-3">
                    <Link
                        to={inquireHref}
                        data-testid={`product-inquire-${product.id}`}
                        className="btn-primary flex-1 inline-flex items-center justify-center gap-2.5 px-4 py-3 text-[11px] md:text-xs font-bold tracking-[0.18em] uppercase rounded-sm"
                    >
                        <ShieldCheck size={14} strokeWidth={2} />
                        Inquire About This Device
                    </Link>
                    <Link
                        to={product.detailsHref}
                        data-testid={`product-details-${product.id}`}
                        className="inline-flex items-center justify-center h-11 w-11 border border-slate-300 text-slate-600 hover:border-[#0A1428] hover:text-[#0A1428] transition-colors"
                        aria-label={`Learn more about ${product.name}`}
                    >
                        <ArrowRight size={16} strokeWidth={2} />
                    </Link>
                </div>
            </div>
        </div>
    );
};
